import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper, CardSection } from 'foundations-components/transitional/components/Card';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { H2 } from 'foundations-components/transitional/components/H2';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { userInfoSync } from 'hub-http/userInfo';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import CurrentlyScheduledCardInfo from './schedulingOptions/CurrentlyScheduledCardInfo';
import SchedulingUnpublishControls from './schedulingOptions/SchedulingUnpublishControls';
import SchedulingPublishControls from './schedulingOptions/SchedulingPublishControls';
import NothingScheduledControls from './schedulingOptions/NothingScheduledControls';
import { SchedulingSteps } from './schedulingOptions/constants';
import SchedulingTabs from './schedulingOptions/SchedulingTabs';
import { createSimpleDateFromMomentTsIfExists, getDateTimeInMinutes } from '../../../utils/publishingTimeUtils';
import useContentCrmObject from '../hooks/useContentCrmObject';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue } from 'content-management-data-lib';
import { PublishTypes } from '../../../constants/PublishTypes';
import { toMomentPortalTz } from 'foundations-components/transitional/utils/SimpleDate';
import { Link } from 'foundations-components/transitional/components/Link';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const useCurrentStep = () => {
  const {
    isContentCrmObjectScheduled,
    isContentCrmObjectPublished,
    isContentCrmObjectScheduledToBeUnpublished,
    isContentCrmObjectPublishedWithScheduledUpdate
  } = useContentCrmObject();
  const [currentStep, setCurrentStep] = useState({
    schedulePublish: isContentCrmObjectScheduled || isContentCrmObjectPublishedWithScheduledUpdate ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED,
    scheduleUnpublish: isContentCrmObjectScheduledToBeUnpublished ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED
  });
  const actionTypeBodyInfoKey = useMemo(() => ({
    schedulePublish: isContentCrmObjectPublished ? 'scheduleUpdate' : 'schedulePublish',
    scheduleUnpublish: 'scheduleUnpublish'
  }), [isContentCrmObjectPublished]);
  return {
    currentStep,
    updateCurrentStep: (tabType, nextStep) => {
      setCurrentStep(Object.assign({}, currentStep, {
        [tabType]: nextStep
      }));
    },
    actionTypeBodyInfoKey
  };
};
const getTimeInMillis = ({
  date,
  minutes
}) => {
  if (date === null || date === undefined || minutes === undefined) {
    return undefined;
  }
  const unixTime = toMomentPortalTz(date).valueOf();
  const result = unixTime + minutes * 60000;
  return result;
};
const useSchedulingOptions = () => {
  const {
    contentCrmObject,
    isContentCrmObjectPublished,
    isContentCrmObjectScheduled,
    isContentCrmObjectPublishedWithScheduledUpdate
  } = useContentCrmObject();
  const {
    contentCrmObjectPublishDate,
    contentCrmObjectScheduledPublishDate,
    contentCrmObjectScheduledUpdateDate,
    contentCrmObjectScheduledUnpublishDate,
    contentCrmObjectUnpublishExpiryUrl
  } = useMemo(() => {
    if (contentCrmObject) {
      return {
        contentCrmObjectPublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISHED_AT),
        contentCrmObjectScheduledPublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE),
        contentCrmObjectScheduledUpdateDate: isContentCrmObjectPublishedWithScheduledUpdate ? getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE) : undefined,
        contentCrmObjectScheduledUnpublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE),
        contentCrmObjectUnpublishExpiryUrl: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL)
      };
    }
    return {};
  }, [contentCrmObject, isContentCrmObjectPublishedWithScheduledUpdate]);
  const [scheduledPublishDate, setScheduledPublishDate] = useState(() => {
    if (isContentCrmObjectPublishedWithScheduledUpdate) {
      return createSimpleDateFromMomentTsIfExists(contentCrmObjectScheduledUpdateDate);
    }
    if (isContentCrmObjectScheduled) {
      return createSimpleDateFromMomentTsIfExists(contentCrmObjectScheduledPublishDate);
    }
  });
  const [scheduledPublishTime, setScheduledPublishTime] = useState(() => {
    if (isContentCrmObjectPublishedWithScheduledUpdate) {
      return getDateTimeInMinutes(contentCrmObjectScheduledUpdateDate);
    }
    if (isContentCrmObjectScheduled) {
      return getDateTimeInMinutes(contentCrmObjectScheduledPublishDate);
    }
  });
  const [scheduledUnpublishDate, setScheduledUnpublishDate] = useState(() => {
    return createSimpleDateFromMomentTsIfExists(contentCrmObjectScheduledUnpublishDate);
  });
  const [scheduledUnpublishTime, setScheduledUnpublishTime] = useState(() => {
    return getDateTimeInMinutes(contentCrmObjectScheduledUnpublishDate);
  });
  const scheduledPublishDateTime = getTimeInMillis({
    date: scheduledPublishDate,
    minutes: scheduledPublishTime
  });
  const scheduledUnpublishDateTime = getTimeInMillis({
    date: scheduledUnpublishDate,
    minutes: scheduledUnpublishTime
  });
  const [unpublishExpiryUrl, setUnpublishExpiryUrl] = useState(contentCrmObjectUnpublishExpiryUrl);
  const minimumDateTimeForUnpublish = useMemo(() => {
    if (isContentCrmObjectPublished) {
      const currentPublishDate = createSimpleDateFromMomentTsIfExists(contentCrmObjectPublishDate);
      const areSelectedDatesSame = currentPublishDate && scheduledUnpublishDate && currentPublishDate.year === scheduledUnpublishDate.year && currentPublishDate.month === scheduledUnpublishDate.month && currentPublishDate.date === scheduledUnpublishDate.date;
      return {
        date: currentPublishDate,
        time: areSelectedDatesSame ? getDateTimeInMinutes(contentCrmObjectPublishDate) : undefined
      };
    }
    if (isContentCrmObjectScheduled || scheduledPublishDateTime) {
      const currentScheduledPublishDate = createSimpleDateFromMomentTsIfExists(scheduledPublishDateTime);
      const areSelectedDatesSame = currentScheduledPublishDate && scheduledUnpublishDate && currentScheduledPublishDate.year === scheduledUnpublishDate.year && currentScheduledPublishDate.month === scheduledUnpublishDate.month && currentScheduledPublishDate.date === scheduledUnpublishDate.date;
      return {
        date: currentScheduledPublishDate,
        time: areSelectedDatesSame ? getDateTimeInMinutes(scheduledPublishDateTime) : undefined
      };
    }
  }, [contentCrmObjectPublishDate, isContentCrmObjectPublished, isContentCrmObjectScheduled, scheduledPublishDateTime, scheduledUnpublishDate]);
  return {
    scheduledPublishDate,
    scheduledPublishTime,
    setScheduledPublishDate,
    setScheduledPublishTime,
    scheduledPublishDateTime,
    scheduledUnpublishDate,
    scheduledUnpublishTime,
    setScheduledUnpublishDate,
    setScheduledUnpublishTime,
    scheduledUnpublishDateTime,
    unpublishExpiryUrl,
    setUnpublishExpiryUrl,
    dateTimeCopyOptions: {
      portalTimezone: userInfoSync().portal.timezone.id,
      scheduledTime: I18n.moment(scheduledPublishDateTime)
      // @ts-expect-error portalTz() isn't typed
      .portalTz().format('LLL'),
      unpublishTime: I18n.moment(scheduledUnpublishDateTime)
      // @ts-expect-error portalTz() isn't typed
      .portalTz().format('LLL')
    },
    minimumDateForUnpublish: minimumDateTimeForUnpublish === null || minimumDateTimeForUnpublish === void 0 ? void 0 : minimumDateTimeForUnpublish.date,
    minimumTimeForUnpublish: minimumDateTimeForUnpublish === null || minimumDateTimeForUnpublish === void 0 ? void 0 : minimumDateTimeForUnpublish.time
  };
};
const SchedulingOptionsModal = ({
  schedulingOptionsModalProps: {
    allowBackdating = false,
    canScheduleUnpublish = false,
    dashboardUrl,
    contentTypeHasSavedViews = false,
    unpublishRedirectOptions
  } = {},
  publish,
  schedulePublishUnpublish,
  closeModal,
  openCancelScheduleConfirmModal,
  getSchedulePublishDescription,
  unpublishFooter,
  publishFooter,
  confirmScheduleButtonTextKey
}) => {
  const [currentTabType, setCurrentTabType] = useState('schedulePublish');
  const {
    currentStep,
    updateCurrentStep,
    actionTypeBodyInfoKey
  } = useCurrentStep();
  const {
    contentObjectType
  } = useContentCrmObject();
  const {
    scheduledPublishDate,
    scheduledPublishTime,
    setScheduledPublishDate,
    setScheduledPublishTime,
    scheduledPublishDateTime,
    scheduledUnpublishDate,
    scheduledUnpublishTime,
    setScheduledUnpublishDate,
    setScheduledUnpublishTime,
    scheduledUnpublishDateTime,
    unpublishExpiryUrl,
    setUnpublishExpiryUrl,
    dateTimeCopyOptions,
    minimumDateForUnpublish,
    minimumTimeForUnpublish
  } = useSchedulingOptions();
  const isReadOnlyMode = useSelector(getIsReadOnlyMode);
  const showSchedulingControls = currentStep[currentTabType] === SchedulingSteps.SCHEDULING || currentStep[currentTabType] === SchedulingSteps.RESCHEDULING;
  const handleScheduleButtonClick = () => {
    if (schedulePublishUnpublish && (scheduledPublishDateTime || scheduledUnpublishDateTime)) {
      if (allowBackdating && scheduledPublishDateTime && scheduledPublishDateTime < Date.now()) {
        // Backdate the content by overriding the publish date
        publish(PublishTypes.publish, scheduledPublishDateTime);
      } else {
        // For scheduling publish for draft content
        schedulePublishUnpublish({
          schedulePublishDate: scheduledPublishDateTime,
          scheduleUnpublishDate: scheduledUnpublishDateTime,
          unpublishExpiryUrl
        });
      }
    }
    closeModal();
  };
  const onExitSchedulingClick = () => {
    updateCurrentStep(currentTabType, currentStep[currentTabType] === SchedulingSteps.RESCHEDULING ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED);
  };
  const isScheduledButtonEnabled = useMemo(() => {
    if (currentTabType === 'schedulePublish') {
      return !!scheduledPublishDateTime;
    }
    if (currentTabType === 'scheduleUnpublish') {
      if (unpublishRedirectOptions !== null && unpublishRedirectOptions !== void 0 && unpublishRedirectOptions.allowRedirectOnUnpublish) {
        return !!(scheduledUnpublishDateTime && unpublishExpiryUrl);
      }
      return !!scheduledUnpublishDateTime;
    }
    return false;
  }, [currentTabType, scheduledPublishDateTime, scheduledUnpublishDateTime, unpublishExpiryUrl, unpublishRedirectOptions === null || unpublishRedirectOptions === void 0 ? void 0 : unpublishRedirectOptions.allowRedirectOnUnpublish]);
  const getBaseSchedulingDescription = useMemo(() => {
    if (getSchedulePublishDescription) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [getSchedulePublishDescription(dateTimeCopyOptions.scheduledTime, dateTimeCopyOptions.portalTimezone), currentTabType === 'schedulePublish' && scheduledPublishDateTime && publishFooter, currentTabType === 'scheduleUnpublish' && scheduledUnpublishDateTime && unpublishFooter]
      });
    }
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: `baseSchedulingOptionsModal.body.info.${contentObjectType}.${actionTypeBodyInfoKey[currentTabType]}`,
      options: dateTimeCopyOptions
    });
  }, [contentObjectType, actionTypeBodyInfoKey, dateTimeCopyOptions, currentTabType, getSchedulePublishDescription, publishFooter, scheduledPublishDateTime, scheduledUnpublishDateTime, unpublishFooter]);
  return /*#__PURE__*/_jsxs(Modal, {
    width: 550,
    "data-test-id": "scheduling-options-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "baseSchedulingOptionsModal.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: closeModal
      })]
    }), /*#__PURE__*/_jsx(SchedulingTabs, {
      canScheduleUnpublish: canScheduleUnpublish,
      tabType: currentTabType,
      handleTabChange: setCurrentTabType,
      currentStep: currentStep,
      scheduledPublishDateTime: scheduledPublishDateTime
    }), /*#__PURE__*/_jsxs(DialogBody, {
      paddingBlockEnd: "400",
      children: [/*#__PURE__*/_jsx(CardWrapper, {
        compact: true,
        children: /*#__PURE__*/_jsxs(CardSection, {
          children: [currentStep[currentTabType] === SchedulingSteps.NOTHING_SCHEDULED ? /*#__PURE__*/_jsx(NothingScheduledControls, {
            actionTypeBodyInfoKey: actionTypeBodyInfoKey[currentTabType],
            onScheduleClick: () => {
              updateCurrentStep(currentTabType, SchedulingSteps.SCHEDULING);
            }
          }) : null, showSchedulingControls ? currentTabType === 'schedulePublish' ? /*#__PURE__*/_jsx(SchedulingPublishControls, {
            scheduledPublishDate: scheduledPublishDate,
            setPublishDateDay: setScheduledPublishDate,
            publishDateTime: scheduledPublishTime,
            setPublishDateTime: setScheduledPublishTime,
            isReadOnlyMode: isReadOnlyMode,
            allowBackdating: allowBackdating,
            maxPublishDate: scheduledUnpublishDate,
            onExitSchedulingClick: onExitSchedulingClick
          }) : canScheduleUnpublish ? /*#__PURE__*/_jsx(SchedulingUnpublishControls, {
            scheduledUnpublishDate: scheduledUnpublishDate,
            setScheduledUnpublishDate: setScheduledUnpublishDate,
            scheduledUnpublishTime: scheduledUnpublishTime,
            setScheduledUnpublishTime: setScheduledUnpublishTime,
            isReadOnlyMode: isReadOnlyMode,
            onExitSchedulingClick: onExitSchedulingClick,
            minimumDateForUnpublish: minimumDateForUnpublish,
            minimumTimeForUnpublish: minimumTimeForUnpublish,
            unpublishExpiryUrl: unpublishExpiryUrl,
            setUnpublishExpiryUrl: setUnpublishExpiryUrl,
            unpublishRedirectOptions: unpublishRedirectOptions
          }) : null : null, currentStep[currentTabType] === SchedulingSteps.SCHEDULED ? /*#__PURE__*/_jsx(CurrentlyScheduledCardInfo, {
            actionTypeBodyInfoKey: actionTypeBodyInfoKey[currentTabType],
            dateTimeCopyOptions: dateTimeCopyOptions,
            onReschedulingClick: () => {
              updateCurrentStep(currentTabType, SchedulingSteps.RESCHEDULING);
            },
            openCancelScheduleConfirmModal: openCancelScheduleConfirmModal
          }) : null]
        })
      }), showSchedulingControls && (currentTabType === 'schedulePublish' ? scheduledPublishDateTime : scheduledUnpublishDateTime) ? getBaseSchedulingDescription : null, currentStep[currentTabType] === SchedulingSteps.SCHEDULED && currentTabType === 'schedulePublish' && contentTypeHasSavedViews && dashboardUrl ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `baseSchedulingOptionsModal.body.findScheduledContent.${contentObjectType}.${actionTypeBodyInfoKey[currentTabType]}_jsx`,
        options: {
          href: `${dashboardUrl}/all-scheduled`
        },
        elements: {
          UILink: Link
        }
      }) : null]
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: showSchedulingControls ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          onClick: handleScheduleButtonClick,
          disabled: !isScheduledButtonEnabled,
          "data-test-id": "schedule-button",
          children: currentStep[currentTabType] === SchedulingSteps.RESCHEDULING ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "baseSchedulingOptionsModal.footerButtons.reschedule"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: confirmScheduleButtonTextKey || 'baseSchedulingOptionsModal.footerButtons.schedule'
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: closeModal,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "baseSchedulingOptionsModal.footerButtons.cancel"
          })
        })]
      }) : null
    }), /*#__PURE__*/_jsx(NavMarker, {
      name: "PUBLISHING_TAB_RENDERED"
    })]
  });
};
export default SchedulingOptionsModal;